// localStream.js
// ★ 本地流处理器：完整克隆 StreamProcessor.fetchAndProcessData，
//   仅删除两处 store.dispatch('updateMessageToConversation')，
//   供「学习详情」弹窗使用 —— 消息自始至终不进主窗口 store，
//   从源头杜绝交互文本（诊断/闯关/回复选项）在主窗口出现副本。
import { getLevel } from '@/utils/chat/chatUtil';
import { getToken, getUser } from '@/utils/auth';
import { getBaseURL } from '@/utils/request';

// ★ 转发导出，供 streamResume.js 使用（避免循环依赖可直接从 '@/utils/request' 导）
export { getBaseURL };

export const CONTENT_TYPE_JSON = "application/json;charset=utf-8";

// ★★★ 公共分片解析器：主接口与续传接口共用，保证 offset 口径唯一 ★★★
// 后端契约：每次 yield 一个完整 JSON + '\n'；json.dumps 已转义字符串内换行，
// 因此裸 '\n' 只出现在分片边界，按行切分最稳。
// 返回 { parsed: [json对象...], rest: '未完成尾巴' }。
//   - 非 flush：最后一段（可能不完整）原样放入 rest，等待下一块
//   - flush=true：流结束时调用，把最后一段也当完整行解析
export function parseChunkBuffer(buf, flush = false) {
  const lines = buf.split('\n');

  let rest = '';
  if (flush) {
    // 流结束：若末尾为空行（正常以 \n 收尾）则丢弃，否则最后一段也算一行
    if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  } else {
    // 末段可能是不完整记录，留给下一块
    rest = lines.pop() ?? '';
  }

  const parsed = [];
  try {
    for (const line of lines) {
      const s = line.trim();
      if (!s) continue;
      parsed.push(JSON.parse(s));
    }
    return { parsed, rest };
  } catch (e) {
    // 契约被破坏（某行不是合法 JSON）：原样保留，等下一块或上层报错
    return { parsed: null, rest: buf };
  }
}

// ★ 本地错误处理：克隆 FetchErrorHandler 的状态写入逻辑，
//   去掉 store.dispatch 与全局路由跳转/踢登录（弹窗流不干预全局会话）
function localSetErrorMessage(status, aiContent) {
  if (!['', 'loading'].includes(aiContent.msg)) return;
  aiContent.msg = "";
  aiContent.isDone = status;
  const messages = {
    2: '请求已取消',   // ABORT
    3: '网络出错',     // NETWORK
    401: '登录过期',   // EXPIRE（本地流仅标记，不踢登录）
    500: '服务器异常', // INTERAL
  };
  aiContent.msg = messages[status] || '服务器异常';
  if (aiContent.parts.length === 0) {
    aiContent.parts.push({ msg: aiContent.msg, questions: {} });
  }
}

export async function fetchLocalStream(url, data, handleDataFunction) {
  let readTimeout = null;
  let responseChunk = '';
  let { question, aiContent, params } = data;
  let options = {
    method: 'POST',
    signal: params.signal,
  };
  options.headers = new Headers({
    "Content-Type": CONTENT_TYPE_JSON,
    "Authorization": getToken(),
    "username": getUser() && getUser().phoneNumber,
  });

  let level = "";
  const withOutLevelUrls = ["/Composition"];
  if (question && question.level) { level = question.level; }
  if (!level) { level = getLevel(); }
  if (!withOutLevelUrls.some(target => url.includes(target))) {
    const { signal, ...bodyParams } = params;
    options.body = JSON.stringify({ ...bodyParams, level });
  }

  // ★ 复用同一 decoder：跨 chunk 保留多字节解码状态
  const decoder = new TextDecoder('utf-8');

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      console.log(response.status);
      localSetErrorMessage(response.status || 3, aiContent);
      return;   // ★ 错误态直接返回，防止对错误响应继续读流
    }
    const reader = response.body.getReader();

    async function readChunk() {
      const { done, value } = await reader.read();
      if (done) {
        console.log('全部数据接收完毕');

        // ★ flush 尾部残留：处理末尾没有换行的最后一条记录
        if (responseChunk) {
          const { parsed } = parseChunkBuffer(responseChunk, true);
          if (parsed) {
            for (const json of parsed) {
              if (json && json.hb) {
                if (aiContent) aiContent._lastHb = Date.now();
                if (question) question._lastHb = Date.now();
                continue;
              }
              handleDataFunction(json, aiContent, question);
              if (aiContent) {
                aiContent._receivedCount = (aiContent._receivedCount || 0) + 1;
              }
            }
          }
          responseChunk = '';
        }

        if (aiContent) {
          aiContent.isDone = 1;
          if (aiContent.msg === "loading") { aiContent.msg = ""; }
        }
        clearTimeout(readTimeout);
        if (question) { question.isLoading = false; }
        reader.releaseLock();
        return;
      }
      clearTimeout(readTimeout);
      // ★ stream: true 防止多字节字符被切在 chunk 边界时产出替换字符
      responseChunk += decoder.decode(value, { stream: true });

      // ★ 公共解析器解析 + offset 计数
      const { parsed, rest } = parseChunkBuffer(responseChunk);
      if (parsed === null) return readChunk();     // 半截分片，等下一块再拼
      responseChunk = rest;

      for (const json of parsed) {
        // ★ 心跳帧：连接保活信号，不喂处理器、不计入 offset
        if (json && json.hb) {
          if (aiContent) aiContent._lastHb = Date.now();
          if (question) question._lastHb = Date.now();
          continue;
        }
        handleDataFunction(json, aiContent, question);
        // ★ offset 计数：每成功处理 1 个完整 JSON 分片 +1
        aiContent._receivedCount = (aiContent._receivedCount || 0) + 1;
      }
      return readChunk();
    }

    readChunk();
  } catch (error) {
    if (aiContent?._aborted) {
      clearTimeout(readTimeout);
      return;
    }
    question && (question.isLoading = false);
    if (!aiContent) return;
    aiContent.isSlow = false;
    if (error.name === 'AbortError') {
      localSetErrorMessage(2, aiContent);
      console.log('Request aborted:', error);
    } else {
      localSetErrorMessage(3, aiContent);
      console.error('Request failed:', error);
    }
  } finally {
    clearTimeout(readTimeout);
  }
}